import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconButton from 'components/common/IconButton';
import { ProductContext } from 'context/Context';
import useProductHook from './useProductHook';
import ProductImage from './ProductImage';
import StarRating from 'components/common/StarRating';
import Flex from 'components/common/Flex';
import { useTranslation, Trans } from 'react-i18next';
import y from 'y4/Utils';
import CopyButton from './CopyButton';

const ProductList = ({ product, index }) => {
  const { t } = useTranslation('commerce');

  const {
    name,
    category,
    id,
    features,
    price,
    discount,
    salePrice,
    shippingCost,
    rating,
    totalReview,
    isInStock,
    stock,
    isNew,
    files
  } = product;

  const {
    isInFavouriteItems,
    productsState: { config }
  } = useContext(ProductContext);
  const currency = config.currency;

  const params = useParams();
  const { handleAddToCart, handleFavouriteClick } = useProductHook(product);

  return (
    <>
      <Col
        xs={12}
        className={classNames('p-x1', {
          'bg-100': index % 2 !== 0
        })}
      >
        <Row>
          <Col sm={5} md={4}>
            <ProductImage
              name={name}
              id={id}
              isNew={isNew}
              files={files}
              layout="list"
            />
          </Col>
          <Col sm={7} md={8}>
            <Row className="h-100">
              <Col lg={8}>
                <h5 className="mt-3 mt-sm-0">
                  <Link
                    to={`/${params.corp}/e-commerce/product/product-details/${id}`}
                    className="text-dark fs-0 fs-lg-1"
                  >
                    {name}
                  </Link>
                </h5>
                <p className="fs--1 mb-2 mb-md-3">
                  <Link to="#!" className="text-500">
                    {category}
                  </Link>
                </p>
                {y.isEmpty(features) ? null : (
                  <ul className="list-unstyled d-none d-lg-block">
                    {features.map(feature => (
                      <li key={feature}>
                        <FontAwesomeIcon icon="circle" transform="shrink-12" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Col>
              <Col lg={4} as={Flex} justifyContent="between" direction="column">
                <div>
                  <h4 className="fs-3 fs-md-3 text-warning mb-0">
                    {`${currency}${salePrice ? salePrice : price}`}
                  </h4>
                  {salePrice ? (
                    <h5 className="fs-0 fst-italic text-500 mb-0 mt-1">
                      <del>{`${isNaN(price) ? '' : currency}${price}`}</del>
                      <span className="ms-2">{discount}</span>
                    </h5>
                  ) : null}
                  <div className="mb-2 mt-3">
                    <StarRating readonly rating={rating} />
                    {config.withReview ? (
                      <span className="ms-1">({totalReview})</span>
                    ) : null}
                  </div>
                  <div className="d-none d-lg-block">
                    {shippingCost ? (
                      <p className="fs--1 mb-1">
                        {isNaN(shippingCost) ? (
                          <strong>{shippingCost}</strong>
                        ) : (
                          <>
                            <Trans ns="commerce">Shipping Cost</Trans> :{' '}
                            <strong>{`${currency}${shippingCost}`}</strong>{' '}
                          </>
                        )}
                      </p>
                    ) : null}
                    <p className="fs--1 mb-1">
                      <Trans ns="commerce">Stock</Trans> :{' '}
                      <strong
                        className={classNames({
                          'text-success': isInStock,
                          'text-danger': !isInStock
                        })}
                      >
                        {stock
                          ? stock
                          : isInStock
                          ? t('Available')
                          : t('Sold-Out')}
                      </strong>
                    </p>
                    <CopyButton product={product} />
                  </div>
                </div>
                <div className="mt-2">
                  <IconButton
                    disabled={!config.withFavorite}
                    size="sm"
                    variant={
                      isInFavouriteItems(id)
                        ? 'outline-danger'
                        : 'outline-secondary'
                    }
                    className={classNames('d-lg-block me-2 me-lg-0 w-lg-100', {
                      'border-300': !isInFavouriteItems(id)
                    })}
                    icon={isInFavouriteItems(id) ? 'heart' : ['far', 'heart']}
                    onClick={handleFavouriteClick}
                  >
                    <Trans ns="commerce">Favourite</Trans>
                  </IconButton>
                  <IconButton
                    disabled={!config.withCart}
                    size="sm"
                    variant="primary"
                    className="d-lg-block mt-lg-2 w-lg-100"
                    icon="cart-plus"
                    onClick={() => handleAddToCart(1, true, true)}
                  >
                    <Trans ns="commerce">Add to Cart</Trans>
                  </IconButton>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </>
  );
};

ProductList.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    features: PropTypes.array,
    price: PropTypes.number.isRequired,
    discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    salePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shippingCost: PropTypes.number,
    rating: PropTypes.number,
    totalReview: PropTypes.number,
    isInStock: PropTypes.bool,
    stock: PropTypes.string,
    isNew: PropTypes.any,
    files: PropTypes.arrayOf(PropTypes.string).isRequired
  }),
  index: PropTypes.number
};

export default ProductList;
